import {
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import {
	ExploreOutlined,
	InfoOutlined,
	PlayCircleOutline,
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import axios from 'axios';
import phone from 'phone';
import { createActivityLog } from 'src/graphql/mutations';
import {
	activityLogSuppressionSearch,
	contactFieldSuppressionByList,
	dncByList,
	expandedContactSearch,
	PocMeta,
	segmentByTenant
} from 'src/graphql/queries';
import { UserContext } from '../../../contexts/UserContext';
import DNCDotCom from '../../../models/dnc';
import evaluateDotPath from '../../../tools/evaluateDotPath';
import { responseTypes, stringTransformations } from './restService';
const moment = require('moment');

// These exist to normalize values between POC metadata and the filter expression.
const booleanFieldTransformer = (value) => !!value;
const transformers = {
	'poc.writtenConsent': (value) => {
		if (typeof value === 'string' && value.includes('now')) {
			const now = new Date();
			now.setDate(
				now.getDate() + /\d+/.exec(value)[0] * (value[3] === '-' ? -1 : 1),
			);
			return now;
		}

		return new Date(value);
	},
	'poc.cell': booleanFieldTransformer,
	'poc.consent': booleanFieldTransformer,
	'poc.optOut': booleanFieldTransformer,
	'poc.preferred': booleanFieldTransformer,
	'poc.thirdParty': booleanFieldTransformer,
};

export default function Tester(props) {
	const client = generateClient();
	const { formikProps, logs, setLogs, dncDotComSettings } = props;

	const userContext = useContext(UserContext);
	const [loadingSegments, setLoadingSegments] = useState(false);
	const [selectedSegment, setSelectedSegment] = useState('');
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [contactTestMap, setContactTestMap] = useState({});
	const [availablePocFieldsToTest, setAvailablePocFieldsToTest] = useState([]);
	const [cadenceField, setCadenceField] = useState('phone');
	const [segments, setSegments] = useState([]);

	const contactTableColumns = [
		{ header: 'External ID', dynamo: 'externalId' },
		{ header: 'Name', dynamo: 'firstName|lastName' },
		{ header: 'Phone', dynamo: 'phone' },
		{ header: 'Cell', dynamo: 'cell' },
	];

	useEffect(() => {
		setAvailablePocFieldsToTest([
			'phone',
			'cell',
			'email',
			...userContext.customFields.filter((x) => x.isPoc).map((x) => x.name),
		]);
		if (userContext.tenantId) {
			getSegments();
		}
	}, [userContext]);

	useEffect(() => {
		async function getContacts() {
			setLoadingContacts(true);
			for (const key in contactTestMap) delete contactTestMap[key];
			const segment = segments.find((x) => x.id === selectedSegment);
			const contacts = await client.graphql({
				query: expandedContactSearch,
				variables: {
					filter: segment.query.searchFilter,
					limit: 10,
				},
			});

			if (contacts?.data?.expandedContactSearch?.items) {
				for (const contact of contacts.data.expandedContactSearch.items) {
					contactTestMap[contact.id] = contact;
				}

				setContactTestMap({ ...contactTestMap });
			}

			setLoadingContacts(false);
		}

		if (selectedSegment) {
			getContacts();
		}
	}, [selectedSegment]);

	useEffect(() => {
		clearContactsTested();
	}, [formikProps.dirty]);

	async function getSegments() {
		setLoadingSegments(true);

		const savedSegments = await client.graphql({
			query: segmentByTenant,
			variables: { tenant: userContext.tenantId },
		});
		if (savedSegments && savedSegments.data) {
			setSegments(
				savedSegments.data.segmentByTenant.items.sort(
					(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
				),
			);
		}

		setLoadingSegments(false);
	}

	function clearContactsTested() {
		for (const id in contactTestMap) {
			if (contactTestMap[id]?.suppression) {
				contactTestMap[id].suppression.activityLog.message = 'Not Tested';
				contactTestMap[id].suppression.activityLog.class = 'notTested';

				contactTestMap[id].suppression.poc.message = 'Not Tested';
				contactTestMap[id].suppression.poc.class = 'notTested';

				contactTestMap[id].suppression.rest.message = 'Not Tested';
				contactTestMap[id].suppression.rest.class = 'notTested';

				contactTestMap[id].suppression.dnc.message = 'Not Tested';
				contactTestMap[id].suppression.dnc.class = 'notTested';
			}
		}

		setContactTestMap({ ...contactTestMap });
	}

	function fillPlaceholders(contact, string) {
		return string.replace(/{{.*?}}/g, (match) => {
			let trimmed = match.substring(2, match.length - 2);
			if (trimmed.startsWith('custom:')) {
				trimmed = trimmed.replace('custom:', '');
				const safeCustomFields = ensureJSON(contact.customFields);
				if (trimmed in safeCustomFields) {
					return safeCustomFields[trimmed]
						? safeFormatPlaceholder(safeCustomFields[trimmed])
						: '';
				}
			} else {
				if (trimmed in contact) {
					return contact[trimmed]
						? safeFormatPlaceholder(contact[trimmed])
						: '';
				}
			}

			return match;
		});
	}

	function ensureJSON(value) {
		if (typeof value === 'string') {
			return JSON.parse(value);
		}

		return value;
	}

	function safeFormatPlaceholder(value) {
		if (typeof value === 'object') {
			return JSON.stringify(value);
		} else {
			return value;
		}
	}

	function evaluateFilterExpressionAgainstObject(query, source) {
		for (const key in query) {
			// This should only ever loop once.
			const lowercaseKey = key.toLowerCase();

			if (lowercaseKey === 'or' || lowercaseKey === 'and') {
				// If the key is a combinator.
				const isAnd = lowercaseKey === 'and';

				for (let i = 0; i < query[key].length; ++i) {
					const ruleResult = evaluateFilterExpressionAgainstObject(
						query[key][i],
						source,
					);
					if (ruleResult === 'trim') {
						// Trim an empty ruleset.
						query[key].splice(i--, 1);
					} else if (ruleResult ^ isAnd) {
						return !isAnd;
					}
				}

				// If the rule set is empty, return "trim" so that the parent can trim it from the tree.
				if (query[key].length === 0) return 'trim';

				return isAnd;
			} else {
				// If the key is a dot path.
				const objectValue = transformers[key](
					key.split('.').reduce((currentObject, currentPathKey) => {
						if (
							typeof currentObject === 'object' &&
							currentObject != null &&
							currentPathKey in currentObject
						) {
							return currentObject[currentPathKey];
						}

						throw key;
					}, source),
				);

				const operator = Object.keys(query[key])[0];
				const queryValue = transformers[key](query[key][operator]);

				switch (operator) {
					case 'eq':
						return objectValue == queryValue; // eslint-disable-line eqeqeq
					case 'ne':
						return objectValue != queryValue; // eslint-disable-line eqeqeq
					case 'lt':
						return objectValue < queryValue;
					case 'gt':
						return objectValue > queryValue;
					case 'lte':
						return objectValue <= queryValue;
					case 'gte':
						return objectValue >= queryValue;
					default:
						return false;
				}
			}
		}
	}

	async function writeToActivityLog(title, body, contactId, type) {
		await client.graphql({
			query: createActivityLog,
			variables: {
				input: {
					tenant: userContext.tenantId,
					type,
					title,
					body,
					activityLogContactId: contactId,
					agent: 'System',
					timestamp: new Date().toISOString(),
					contactStatus: `${type}#suppressed#${moment().toISOString(true)}`,
					ttl: Math.floor(Date.now() / 1000) + 90 * 24 * 60 * 60 // 90 days
				},
			},
		});
	}

	/**
 * Utility: detect which suppression type is present in a check object.
 * Expects exactly one non-null field among the known suppression keys.
 */
	function detectCheckType(check) {
		const keys = [
			'activityLogQuerySuppression',
			'pocQuerySuppression',
			'restSuppression',
			'dncListSuppressions',
			'dncDotCom',
			'contactFieldSuppressions'
		];
		for (const key of keys) {
			if (check[key] != null) return key;
		}
		return null;
	}

	/**
	 * Handlers for each suppression type.
	 * Each returns an object { suppressed: boolean, descriptionPostfix?: string, dispositionOverride?: object, nextWindowTime?: number }
	 * If suppressed === true, caller will send suppression and stop further checks.
	 */

	async function handleActivityLogQuerySuppression(checkData, contact) {
		// If no check data, nothing to do
		if (!checkData) {
			console.warn("No check data for activity log query suppression.");
			return { suppressed: false };
		}

		// Appsync needs the filter expression to be a JSON string.
		if (!(typeof checkData.query === 'string' || checkData.query instanceof String)) {
			checkData.query = JSON.stringify(checkData.query);
		}

		try {
			const result = await client.graphql({
				query: activityLogSuppressionSearch,
				variables: {
					filter: checkData.query,
					contactId: contact.id,
				},
			});

			if (result.data && result.data.activityLogSuppressionSearch && result.data.activityLogSuppressionSearch.count >= checkData.threshold) {
				return {
					suppressed: true,
					description: " The activity log query exceeded the result count threshold."
				};
			} else {
				return { suppressed: false };
			}
		} catch (err) {
			console.error("Activity log check failed:", err);
			return {
				suppressed: true,
				description: " The activity log query failed."
			};
		}
	}

	async function handlePocQuerySuppression(checkData, contact, pocField, suppressionProfile) {
		if (!checkData) {
			console.warn("No check data for POC query suppression.");
			return { suppressed: false };
		}

		try {
			// Normally, the filter expression would have to be a JSON string but we want it parsed out for our purposes.
			if (typeof checkData.query !== 'object') {
				checkData.query = JSON.parse(checkData.query);
			}

			const result = await client.graphql({
				query: PocMeta,
				variables: {
					tenant: suppressionProfile.tenant,
					contactField: `${contact.id}#${pocField}`
				}
			});

			const sourceObject = result.Items && result.Items[0];
			console.log("sourceObject : ", sourceObject);
			// weekend check
			if ([0, 6].includes((new Date()).getDay()) && sourceObject && sourceObject.poc && sourceObject.poc.dontCallOnWeekends && checkData.suppressIfWeekendAndCantCallOnWeekends) {
				return {
					suppressed: true,
					description: " It is a weekend and this point of contact is not to be dialed on weekends."
				};
			}

			if (Object.keys(checkData.query || {}).length > 0) {
				const firstKeyOfQuery = Object.keys(checkData.query)[0];
				try {
					const evaluationResult = evaluateFilterExpressionAgainstObject(checkData.query[firstKeyOfQuery][3], sourceObject);
					if (evaluationResult !== "trim" && evaluationResult) {
						return {
							suppressed: true,
							description: ` The point of contact query returned a result: ${JSON.stringify(sourceObject && sourceObject.poc)}.`
						};
					} else {
						return { suppressed: false };
					}
				} catch (err) {
					return {
						suppressed: true,
						description: ` The query field '${err}' wasn't found in the source object.`
					};
				}
			}
		} catch (err) {
			console.error("POC query check failed:", err);
			return {
				suppressed: true,
				description: " The point of contact query failed."
			};
		}

		return { suppressed: false };
	}

	async function handleRestSuppression(checkData, contactData) {
		if (!checkData) {
			console.warn("No check data for REST suppression.");
			return { suppressed: false };
		}

		const { headers, paramsString } = generateRESTData(checkData, contactData);

		let result;
		try {
			const URL = `${checkData.endpoint}${paramsString}`;
			switch (checkData.method) {
				default:
				case "GET":
					result = await axios.get(URL, { headers });
					break;
				case "POST":
					result = await axios.post(URL, contactData, { headers });
					break;
			}

			let evaluatedResult = evaluateDotPath(result.data, checkData.response.path);
			if (evaluatedResult != null) {
				switch (checkData.response.type) {
					case responseTypes.Boolean:
						if (typeof evaluatedResult === 'string') {
							evaluatedResult = evaluatedResult.toLocaleLowerCase() !== 'false';
						} else {
							evaluatedResult = Boolean(evaluatedResult);
						}
						break;
					case responseTypes.Number:
						evaluatedResult = Number(evaluatedResult);
						if (isNaN(evaluatedResult)) {
							evaluatedResult = null;
						}
						break;
					case responseTypes.String:
						evaluatedResult = stringTransformations[checkData.response.options.responseStringTransformation].transform(`${evaluatedResult}`);
						break;
					default:
						break;
				}
			}

			if (evaluatedResult != null) {
				if (evaluatedResult === checkData.response.value.value) {
					return {
						suppressed: true,
						description: ` The REST call succeeded and the response value specified by the response key was equivalent to the value specified by the suppression profile.`
					};
				}
			} else if (checkData.suppressOnError) {
				return {
					suppressed: true,
					description: " The provided response key was not found in the response."
				};
			}
		} catch (err) {
			console.error("REST call failed: ", err);
			if (checkData.suppressOnError) {
				return {
					suppressed: true,
					description: " The REST call failed."
				};
			}
		}

		return { suppressed: false };
	}

	/**
	 * dncDotCom suppression: this handler expects checkData to be suppressionProfile.dncDotCom (object)
	 * It will perform scrub, reassignment, and knownLitigator checks, mirroring your original behavior.
	 * On axios errors it sets fatalError via the outer scope (caller must check/propagate)
	 */
	async function handleDncDotComSuppression(checkData, contact, contactData, phoneNumber) {
		if (!checkData || !Globals.projectId || !Globals.loginId) {
			console.warn("No check data or Incomplete DNC.com credentials for DNC.com suppression.");
			return { suppressed: false };
		}

		try {
			if ((checkData.scrub || checkData.state)) {
				const body = new FormData();
				body.append('phoneList', phoneNumber);
				body.append('version', '5');
				body.append('output', 'json');
				const scrubResponse = await axios.post(`${DNCDotCom.ScrubURL}?${DNCDotCom.generateAuthAndProjectIDParams(Globals.loginId, Globals.projectId)}`, body, { headers: { 'Content-Type': `multipart/form-data; boundary=${body._boundary}` } });

				await writeToActivityLog(dynamo, Globals.tenant, "DNC.com scrub API invocation", "The suppression engine called the DNC.com scrub API on this contact.", contact.id, "DNCScrubInvocation");

				if (checkData.scrub && DNCDotCom.ScrubStatusCodes[scrubResponse.data[0].ResultCode]) {
					return {
						suppressed: true,
						description: ` The DNC.com scrub API returned a 'Do Not Call' status for the given phone number. Reason: ${scrubResponse.data[0].Reason}`
					};
				} else if (checkData.state && scrubResponse.data[0].DoNotCallToday == 1) { // eslint-disable-line eqeqeq
					return {
						suppressed: true,
						description: " The DNC.com scrub API returned a 'Do Not Call Today' status for the given phone number which indicates either a holiday or state of emergency declaration."
					};
				}
			}

			if (checkData.numberReassignment) {
				const reassignmentResponse = await axios.get(`${DNCDotCom.ReassignmentURL}?${DNCDotCom.generateAuthAndProjectIDParams(Globals.loginId, Globals.projectId)}&${DNCDotCom.generateReassignmentParams(contactData, phoneNumber, checkData.reassignmentSince)}`);

				await writeToActivityLog(dynamo, Globals.tenant, "DNC.com reassignment API invocation", "The suppression engine called the DNC.com reassignment API on this contact.", contact.id, "DNCReassignmentInvocation");

				if (DNCDotCom.ReassignmentStatusCodes[reassignmentResponse.data.VerificationCode]) {
					return {
						suppressed: true,
						description: " The DNC.com number reassignment API returned a mismatch for the given contact data and the phone number."
					};
				}
			}

			if (checkData.knownLitigator) {
				const litigatorResponse = await axios.get(`${DNCDotCom.LitigatorURL}?${DNCDotCom.generateAuthAndProjectIDParams(Globals.loginId, Globals.projectId)}&phoneList=${phoneNumber}`);

				await writeToActivityLog(dynamo, Globals.tenant, "DNC.com litigator API invocation", "The suppression engine called the DNC.com litigator API on this contact.", contact.id, "DNCLitigatorInvocation");

				if (litigatorResponse.data[0][DNCDotCom.LitigatorKey]) {
					return {
						suppressed: true,
						description: " The DNC.com known litigator API confirmed that the phone number has a known litigator."
					};
				}
			}
		} catch (err) {
			console.error(`An error occurred while calling DNC.com APIs:`, err && err.response);
			// bubble up fatalError true for caller to set global fatal state and push retry suppression for valid list later
			return { suppressed: true, description: " An error occurred while calling DNC.com APIs." };
		}

		return { suppressed: false };
	}

	async function handleDncListSuppression(checkData, e164) {
		if (!checkData || !e164) {
			console.warn("No check data or phone number for DNC List suppression.");
			return { suppressed: false };
		}

		let foundInList = false;

		for (const list of checkData.dncs) {
			const result = await client.graphql({
				query: dncByList,
				variables: {
					dncList: list,
					phoneNumber: {
						eq: e164,
					},
				},
			});

			if (result.data.dncByList.items && result.data.dncByList.items.length > 0) {
				foundInList = true;
				break;
			}
		}

		if (foundInList) {
			return {
				suppressed: true,
				description: " The phone number was found in a DNC list specified by the suppression profile."
			};
		}

		return { suppressed: false };
	}

	async function handleContactFieldSuppression(checkData, contactData) {
		if (!checkData || !contactData) {
			console.warn("No check data for Contact Field suppression.");
			return { suppressed: false };
		}

		for (const listData of checkData.contactFieldSuppressions) {
			// each list is stored as a JSON string
			let list = JSON.parse(listData);
			let contactValue;
			if (list.field.includes('custom:')) {
				const customField = list.field.split(':')[1];
				contactValue = (contactData.customFields || {})[customField];
			} else {
				contactValue = contactData[list.field];
			}

			if (!contactValue) continue;

			const result = await client.graphql({
				query: contactFieldSuppressionByList,
				variables: {
					contactFieldSuppressionList: list.id,
					contactFieldValue: {
						eq: contactValue + ''
					}
				}
			});

			if (result.data.contactFieldSuppressionByList.items && result.data.contactFieldSuppressionByList.items.length > 0) {
				return {
					suppressed: true,
					description: `The contact value of ${contactValue} for contact field ${list.field} was found in the suppression list ${list.id}.`
				};
			}
		}

		return { suppressed: false };
	}

	async function runSuppressionEngine(
		contact,
		checks,
		cadenceField,
		client
	) {
		const results = [];
		for (const check of checks || []) {
			const checkType = detectCheckType(check);
			if (!checkType) continue;

			const checkData = check[checkType];
			let result = { suppressed: false };

			try {
					switch (checkType) {
						case 'activityLogQuerySuppression':
						result = await handleActivityLogQuerySuppression(
							checkData,
							contact,
							client
						);
						break;

					case 'pocQuerySuppression':
						result = await handlePocQuerySuppression(
							checkData,
							contact,
							cadenceField,
							false,
							suppressionProfile
						);
						break;

					case 'restSuppression':
						result = await handleRestSuppression(checkData, contact);
						break;

					case 'dncListSuppressions': {
						const e164 = phone(
							contact[cadenceField] ||
							contact?.customFields?.[cadenceField]
						)?.phoneNumber;

						if (e164) {
							result = await handleDncListSuppression(
								checkData,
								e164,
							);
						}
						break;
					}

					case 'dncDotCom': {
						const e164 = phone(
							contact[cadenceField] ||
							contact?.customFields?.[cadenceField]
						)?.phoneNumber;

						if (e164) {
							const phoneNumber = e164.slice(-10);
							result = await handleDncDotComSuppression(
								checkData,
								contact,
								contact,
								phoneNumber
							);
						}
						break;
					}

					case 'contactFieldSuppressions':
						result = await handleContactFieldSuppression(
							checkData,
							contact
						);
						break;

					default:
						break;
				}
			} catch (err) {
				result = {
					suppressed: true,
					description: `Unexpected error during ${checkType}`,
					error: err,
				};
			}

			results.push({ checkType, result });
		}

		return results;
	}

	async function testContact(id, checks) {
		const contact = contactTestMap[id];

		// mark testing true
		setContactTestMap(prev => ({
			...prev,
			[id]: { ...prev[id], testing: true }
		}));

		const result = await runSuppressionEngine(
			contact,
			checks,
			cadenceField,
			client
		);

		// Update UI state cleanly
		setContactTestMap(prev => ({
			...prev,
			[id]: {
				...prev[id],
				testing: false,
				checkResults: result
			}
		}));
	}

	const checkColumnMap = {
		activityLogQuerySuppression: "Activity Log",
		pocQuerySuppression: "Point of Contact",
		restSuppression: "REST",
		dncListSuppressions: "DNC List",
		dncDotCom: "DNC.com",
		contactFieldSuppressions: "Contact Field"
	};

	return (
		<Card variant="outlined">
			<CardHeader
				title="Suppression Tester"
				titleTypographyProps={{
					variant: 'h6',
					style: { paddingBottom: '7px' },
				}}
				avatar={<ExploreOutlined color="primary" />}
			/>
			<CardContent>
				<Grid container spacing={2} direction="column">
					<Grid item>
						<Grid
							container
							spacing={2}
							alignItems="center"
							alignContent="center"
						>
							<Grid item>
								<FormControl>
									<InputLabel>Segment</InputLabel>
									<Select
										label="Segment"
										sx={{ minWidth: '200px' }}
										value={selectedSegment}
										onChange={(e) => setSelectedSegment(e.target.value)}
									>
										{segments.map((segment) => (
											<MenuItem
												key={segment.id}
												id={segment.id}
												value={segment.id}
											>
												{segment.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item>
								<FormControl>
									<InputLabel>Cadence Field</InputLabel>
									<Select
										label="Cadence Field"
										sx={{ minWidth: '200px' }}
										value={cadenceField}
										onChange={(e) => setCadenceField(e.target.value)}
									>
										{availablePocFieldsToTest.map((poc) => (
											<MenuItem key={poc} value={poc}>
												{poc}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item style={{ flexGrow: 1 }}>
								<Grid
									container
									spacing={1}
									alignItems="center"
									justifyContent="flex-end"
								>
									<Grid item>
										<InfoOutlined color="primary" />
									</Grid>
									<Grid item>
										<Typography variant="body2">
											Only the first 10 contacts for the selected segment are
											shown.
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										{contactTableColumns.map((column, index) => (
											<TableCell key={index}>{column.header}</TableCell>
										))}
										<TableCell>
											<Tooltip title="Test all contacts">
												<div>
													<IconButton
														disabled={
															!formikProps.isValid ||
															Object.keys(contactTestMap).length === 0
														}
														onClick={() => {
															for (const key in contactTestMap)
																testContact(key, formikProps.values.checks);
														}}
													>
														<PlayCircleOutline
															color={
																!formikProps.isValid ||
																	Object.keys(contactTestMap).length === 0
																	? 'disabled'
																	: 'primary'
															}
														/>
													</IconButton>
												</div>
											</Tooltip>
										</TableCell>
										{formikProps.values.checks.map((check, index) => (
											<TableCell key={index}>{`Check ${index + 1}`}</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{Object.keys(contactTestMap).map((id) => (
										<TableRow key={id}>
											{contactTableColumns.map((column, index) => {
												const dynamoList = column.dynamo.split('|');
												let result = '';
												for (const dynamo of dynamoList) {
													result += contactTestMap[id][dynamo] ? contactTestMap[id][dynamo] + ' ' : '';
												}

												return <TableCell key={index}>{result}</TableCell>;
											})}
											<TableCell>
												{contactTestMap[id].testing && (
													<CircularProgress
														variant="indeterminate"
														color="primary"
														size={20}
													/>
												)}
												{!contactTestMap[id].testing && (
													<Tooltip title="Test contact">
														<div>
															<IconButton
																size="small"
																disabled={!formikProps.isValid}
																onClick={() => testContact(id, formikProps.values.checks)}
															>
																<PlayCircleOutline
																	color={
																		!formikProps.isValid
																			? 'disabled'
																			: 'primary'
																	}
																	fontSize="small"
																/>
															</IconButton>
														</div>
													</Tooltip>
												)}
											</TableCell>
											{formikProps.values.checks.map((check, index) => {
												const contact = contactTestMap[id];
												if (contact.testing) {
													return (<TableCell key={check.order}>
														<CircularProgress
															variant="indeterminate"
															color="primary"
															size={20}
														/>
													</TableCell>);
												}

												if (!contact.checkResults) {
													return (<TableCell key={check.order}>
														<Typography variant="body2" color="textSecondary">
															Not Tested
														</Typography>
													</TableCell>);
												}

												console.log("contact.checkResults : ", contact.checkResults);
												return (
													<TableCell key={check.order}>
														{contact.checkResults[index] ? (
															<Typography
																variant="body2"
																color={
																	contact.checkResults[index].result.suppressed
																		? 'error'
																		: 'primary'
																}
															>
																{contact.checkResults[index].result.suppressed
																	? 'Suppressed'
																	: 'Not Suppressed'}
															</Typography>
														) : (
															<Typography variant="body2" color="textSecondary">
																Not Tested
															</Typography>
														)}
													</TableCell>
												);
											})}

										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
}
